<app-impersonation-banner></app-impersonation-banner>

<header class="ceb-header" [class.ceb-header--fr]="currentLanguageIso2 === 'fr'">
  <div class="ceb-header__inner">
    <a class="ceb-header__brand" [href]="absoluteUrl('/')">
      <img class="ceb-header__brand-logo" src="assets/logo.png" alt="Solar PV Schemes" />
    </a>

    <nav class="ceb-header__nav ceb-header__nav--desktop" [attr.aria-label]="'shared.ceb.header.navigation_aria' | translate">
      @for (section of primaryNavSections; track section.key) {
        <div
          class="ceb-nav-dropdown"
          [class.is-open]="openSection === section.key"
          (mouseenter)="openSectionOnHover(section.key)"
          (mouseleave)="closeSectionOnLeave(section.key)"
        >
          <button class="ceb-nav-button font-light" type="button" (click)="toggleSection($event, section.key)">
            {{ section.labelKey | translate }}
            <app-ceb-source-icon icon="chevron-down" [size]="14"></app-ceb-source-icon>
          </button>
          <div class="ceb-nav-menu" [style.min-width.px]="section.menuWidth" (click)="$event.stopPropagation()">
            @for (link of section.links; track link.path) {
              <a class="ceb-nav-card" [href]="resolveNavLink(link.path)" [class.ceb-nav-card--tagged]="!!link.labelKey">
                @if (link.labelKey) {
                  <span class="ceb-nav-card__tag">{{ link.labelKey | translate }}</span>
                } @else {
                  <span class="ceb-nav-card__icon">
                    <app-ceb-source-icon [icon]="link.icon!" [size]="16"></app-ceb-source-icon>
                  </span>
                }
                <span class="ceb-nav-card__body">
                  <span class="ceb-nav-card__title" [class.ceb-nav-card__title--tagged]="!!link.labelKey">{{ link.titleKey | translate }}</span>
                  <span class="ceb-nav-card__subtitle">{{ link.subtitleKey | translate }}</span>
                </span>
              </a>
            }
          </div>
        </div>
      }

      @for (link of quickLinks; track link.path) {
        <a class="ceb-nav-link" [href]="absoluteUrl(link.path)">
          <app-ceb-source-icon [icon]="link.icon" [size]="14"></app-ceb-source-icon>
          {{ link.labelKey | translate }}
        </a>
      }

      @if (resourcesNavSection; as resourcesSection) {
        <div
          class="ceb-nav-dropdown"
          [class.is-open]="openSection === resourcesSection.key"
          (mouseenter)="openSectionOnHover(resourcesSection.key)"
          (mouseleave)="closeSectionOnLeave(resourcesSection.key)"
        >
          <button class="ceb-nav-button font-light" type="button" (click)="toggleSection($event, resourcesSection.key)">
            {{ resourcesSection.labelKey | translate }}
            <app-ceb-source-icon icon="chevron-down" [size]="14"></app-ceb-source-icon>
          </button>
          <div class="ceb-nav-menu" [style.min-width.px]="resourcesSection.menuWidth" (click)="$event.stopPropagation()">
            @for (link of resourcesSection.links; track link.path) {
              <a class="ceb-nav-card" [href]="absoluteUrl(link.path)" [class.ceb-nav-card--tagged]="!!link.labelKey">
                @if (link.labelKey) {
                  <span class="ceb-nav-card__tag">{{ link.labelKey | translate }}</span>
                } @else {
                  <span class="ceb-nav-card__icon">
                    <app-ceb-source-icon [icon]="link.icon!" [size]="16"></app-ceb-source-icon>
                  </span>
                }
                <span class="ceb-nav-card__body">
                  <span class="ceb-nav-card__title" [class.ceb-nav-card__title--tagged]="!!link.labelKey">{{ link.titleKey | translate }}</span>
                  <span class="ceb-nav-card__subtitle">{{ link.subtitleKey | translate }}</span>
                </span>
              </a>
            }
          </div>
        </div>
      }
    </nav>

    <div class="ceb-header__actions ceb-header__actions--desktop">
      @if (showManagerStatusButton()) {
        <button type="button" class="ceb-utility-button" (click)="openManagerApplications($event)" [attr.aria-label]="'shared.ceb.header.manager_status' | translate" [attr.title]="'shared.ceb.header.manager_status' | translate">
          <ion-icon name="time-outline" aria-hidden="true"></ion-icon>
        </button>
      }
      @if (showSaveButton()) {
        <button type="button" class="ceb-utility-button ceb-utility-button--save" (click)="onSave($event)" [attr.aria-label]="saveButtonLabelKey | translate" [attr.title]="saveButtonLabelKey | translate">
          <span class="ceb-save-icon-wrapper">
            <ion-icon name="save-outline" aria-hidden="true"></ion-icon>
            @if (showUnsavedIndicator()) {
              <span class="ceb-save-unsaved-dot" aria-hidden="true"></span>
            }
          </span>
        </button>
      }

      <div
        class="ceb-nav-dropdown ceb-nav-dropdown--lang"
        [class.is-open]="openSection === 'languages'"
        (mouseenter)="openSectionOnHover('languages')"
        (mouseleave)="closeSectionOnLeave('languages')"
      >
        <button class="ceb-lang-button" type="button" (click)="toggleSection($event, 'languages')">
          <img class="ceb-lang-button__flag" [src]="currentLanguageFlag" [alt]="'shared.ceb.header.current_language_alt' | translate" />
          <app-ceb-source-icon icon="chevron-down" [size]="11"></app-ceb-source-icon>
        </button>
        <div class="ceb-lang-menu" (click)="$event.stopPropagation()">
          @for (lang of languages(); track lang.languageISO2) {
            <button type="button" class="ceb-lang-item" (click)="onLanguageChange(lang.languageISO2, $event)">
              <img class="ceb-lang-item__flag" [src]="getFlagUrl(lang.languageISO2)" [alt]="lang.languageFullNative" />
              {{ lang.languageFullNative }}
            </button>
          }
        </div>
      </div>

      <div
        class="ceb-nav-dropdown ceb-nav-dropdown--profile"
        [class.is-open]="openSection === 'profile'"
        (mouseenter)="openSectionOnHover('profile')"
        (mouseleave)="closeSectionOnLeave('profile')"
      >
        <button
          class="ceb-profile-button"
          type="button"
          (click)="toggleSection($event, 'profile')"
          [attr.aria-label]="displayName"
          [attr.title]="displayName"
        >
          <span class="ceb-profile-button__initials">{{ userInitials }}</span>
        </button>
        <div class="ceb-profile-menu" (click)="$event.stopPropagation()">
          <button type="button" class="ceb-profile-menu__item" (click)="openDashboardWeb($event)">
            <ion-icon name="home-outline" aria-hidden="true"></ion-icon>
            <span>{{ 'application.menu.dashboard' | translate }}</span>
          </button>
          <button type="button" class="ceb-profile-menu__item" (click)="onLogout($event)">
            <ion-icon name="log-out-outline" aria-hidden="true"></ion-icon>
            <span>{{ 'application.menu.logout' | translate }}</span>
          </button>
        </div>
      </div>
    </div>

    <button type="button" class="ceb-mobile-toggle" (click)="toggleMobileMenu()" [attr.aria-label]="'shared.ceb.header.menu' | translate">
      <ion-icon [name]="mobileMenuOpen ? 'close' : 'menu-outline'" aria-hidden="true"></ion-icon>
    </button>
  </div>

  @if (mobileMenuOpen) {
    <div class="ceb-mobile-overlay">
      <button type="button" class="ceb-mobile-overlay__backdrop" (click)="closeMobileMenu()" [attr.aria-label]="'shared.ceb.header.menu' | translate"></button>

      <div class="ceb-mobile-panel">
        <div class="ceb-mobile-panel__body">
          @for (section of primaryNavSections; track section.key) {
            <div class="ceb-mobile-group">
              <button class="ceb-mobile-group__toggle" type="button" (click)="toggleSection($event, section.key)">
                <span class="ceb-mobile-group__title">{{ section.labelKey | translate }}</span>
                <app-ceb-source-icon icon="chevron-down" [size]="14"></app-ceb-source-icon>
              </button>

              @if (openSection === section.key) {
                <div class="ceb-mobile-group__links">
                  @for (link of section.links; track link.path) {
                    <a class="ceb-mobile-card" [class.ceb-mobile-card--icon]="!link.labelKey" [href]="resolveNavLink(link.path)" (click)="closeMobileMenu()">
                      @if (link.labelKey) {
                        <span class="ceb-mobile-card__tag">{{ link.labelKey | translate }}</span>
                      } @else {
                        <span class="ceb-mobile-card__icon">
                          <app-ceb-source-icon [icon]="link.icon!" [size]="16"></app-ceb-source-icon>
                        </span>
                      }

                      <span class="ceb-mobile-card__body">
                        <span class="ceb-mobile-card__title">{{ link.titleKey | translate }}</span>
                        <span class="ceb-mobile-card__subtitle">{{ link.subtitleKey | translate }}</span>
                      </span>
                    </a>
                  }
                </div>
              }
            </div>
          }

          <div class="ceb-mobile-shortcuts">
            @for (link of quickLinks; track link.path) {
              <a class="ceb-mobile-shortcut" [href]="absoluteUrl(link.path)" (click)="closeMobileMenu()">
                <span class="ceb-mobile-shortcut__content">
                  <app-ceb-source-icon [icon]="link.icon" [size]="14"></app-ceb-source-icon>
                  {{ link.labelKey | translate }}
                </span>
              </a>
            }
          </div>

          @if (resourcesNavSection; as resourcesSection) {
            <div class="ceb-mobile-group">
              <button class="ceb-mobile-group__toggle" type="button" (click)="toggleSection($event, resourcesSection.key)">
                <span class="ceb-mobile-group__title">{{ resourcesSection.labelKey | translate }}</span>
                <app-ceb-source-icon icon="chevron-down" [size]="14"></app-ceb-source-icon>
              </button>

              @if (openSection === resourcesSection.key) {
                <div class="ceb-mobile-group__links">
                  @for (link of resourcesSection.links; track link.path) {
                    <a class="ceb-mobile-card ceb-mobile-card--icon" [href]="absoluteUrl(link.path)" (click)="closeMobileMenu()">
                      <span class="ceb-mobile-card__icon">
                        <app-ceb-source-icon [icon]="link.icon!" [size]="16"></app-ceb-source-icon>
                      </span>

                      <span class="ceb-mobile-card__body">
                        <span class="ceb-mobile-card__title">{{ link.titleKey | translate }}</span>
                        <span class="ceb-mobile-card__subtitle">{{ link.subtitleKey | translate }}</span>
                      </span>
                    </a>
                  }
                </div>
              }
            </div>
          }

          <div class="ceb-mobile-group ceb-mobile-group--languages">
            <div class="ceb-mobile-group__heading">
              <span class="ceb-mobile-group__title">{{ 'shared.ceb.header.language' | translate }}</span>
            </div>

            <div class="ceb-mobile-languages">
              @for (lang of languages(); track lang.languageISO2) {
                <button
                  type="button"
                  class="ceb-mobile-language"
                  [class.is-active]="currentLanguageIso2 === lang.languageISO2"
                  (click)="onLanguageChange(lang.languageISO2, $event)"
                >
                  <img class="ceb-mobile-language__flag" [src]="getFlagUrl(lang.languageISO2)" [alt]="lang.languageFullNative" />
                  <span>{{ lang.languageFullNative }}</span>
                </button>
              }
            </div>
          </div>

          <div class="ceb-mobile-actions">
            @if (showManagerStatusButton()) {
              <button type="button" class="ceb-mobile-action" (click)="openManagerApplications($event)" [attr.aria-label]="'shared.ceb.header.manager_status' | translate">
                <ion-icon name="time-outline" aria-hidden="true"></ion-icon>
              </button>
            }
            @if (showSaveButton()) {
              <button type="button" class="ceb-mobile-action ceb-mobile-action--save" (click)="onSave($event)" [attr.aria-label]="saveButtonLabelKey | translate">
                <span class="ceb-save-icon-wrapper">
                  <ion-icon name="save-outline" aria-hidden="true"></ion-icon>
                  @if (showUnsavedIndicator()) {
                    <span class="ceb-save-unsaved-dot" aria-hidden="true"></span>
                  }
                </span>
                <span>{{ saveButtonLabelKey | translate }}</span>
              </button>
            }
            <button type="button" class="ceb-mobile-action" (click)="openDashboardWeb($event)" [attr.aria-label]="'shared.ceb.header.open_dashboard' | translate">
              <ion-icon name="home-outline" aria-hidden="true"></ion-icon>
              <span>{{ 'shared.ceb.header.open_dashboard' | translate }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</header>
